import * as types from '../sieve/types';
import { axios } from '~/util/http/axioshttp';
import { useSuspenseQuery, type UseSuspenseQueryOptions } from '@tanstack/react-query';
import type { ProblemDetails } from '~/util/http/types';
import type { AxiosError } from 'axios';
import { type SearchOptionType, type SearchTypeToModel } from '@generated';
import { getSearchQueryKeyWithFilters } from './useSearch';

export interface useSuspenseSearchProps<K extends SearchOptionType>
	extends types.SieveSearchModel,
		Omit<UseSuspenseQueryOptions<{ collection: SearchTypeToModel[K][]; count: number }>, 'queryKey' | 'query'> {
	searchType: K;
	onSuccess?: (data: { collection: SearchTypeToModel[K][]; count: number }) => void;
}
interface useSuspenseSearchResult<T> {
	data: T[];
	count: number;
	isError: boolean;
	error: AxiosError<ProblemDetails, T> | null;
	refetch: () => void;
	isFetching: boolean;
	sortColumn?: string | null;
	sortDirection?: string | null;
}

export function useSuspenseSearch<K extends SearchOptionType>(props: useSuspenseSearchProps<K>): useSuspenseSearchResult<SearchTypeToModel[K]> {
	const { queryKey, url, sortColumn, sortDirection } = getSearchQueryKeyWithFilters(props);

	const { data, isError, error, refetch, isFetching } = useSuspenseQuery<{ collection: SearchTypeToModel[K][]; count: number }>({
		queryFn: async () => {
			const resp = await axios.get(url);
			if (props.onSuccess) {
				props.onSuccess(resp.data);
			}
			return resp.data;
		},
		queryKey,
		staleTime: 3000,
		gcTime: 0,
		...props
	});

	if (isError) {
		return { data: [], count: 0, isError, error, refetch, isFetching };
	}

	const count = data.count;

	return {
		data: count > 0 ? data.collection : [],
		count,
		isError,
		error,
		refetch,
		isFetching,
		sortColumn,
		sortDirection
	};
}
